import {
  useInternalIntegrationIndividualTestingReportRetrieveSuspense,
  useInternalIntegrationIndividualTestingReportTestComparisonTableSuspense,
  useInternalIntegrationIndividualTestingReportTestRecordTableSuspense,
} from "gen/endpoints/integration-testing-reports/integration-testing-reports";
import { GenericTableTextBadge } from "gen/model/genericTableTextBadge";
import { IntegrationTestStatusEnum } from "gen/model/integrationTestStatusEnum";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonTooltip,
} from "shared/components/button/ToggleButton";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { naiveFormatEnumValue } from "shared/utils/formattingUtils";

const getIndividualTestingReportBadge = (
  status: IntegrationTestStatusEnum | undefined | null,
): GenericTableTextBadge | undefined => {
  if (!status) {
    return undefined;
  }
  switch (status) {
    case IntegrationTestStatusEnum.pending:
      return {
        type: "text",
        color: "warning",
        text: naiveFormatEnumValue(status),
      };
    case IntegrationTestStatusEnum.success:
      return {
        type: "text",
        color: "success",
        text: naiveFormatEnumValue(status),
      };
    case IntegrationTestStatusEnum.failure:
      return {
        type: "text",
        color: "error",
        text: naiveFormatEnumValue(status),
      };
    case IntegrationTestStatusEnum.error:
      return {
        type: "text",
        color: "error",
        text: naiveFormatEnumValue(status),
      };
  }
};

interface LeftJoinToggleButtonProps {
  isLeftJoin: boolean;
  setIsLeftJoin: (isEqualizedView: boolean) => void;
  size?: "sm" | "lg";
}

const IsLeftJoinToggleButton: React.FC<LeftJoinToggleButtonProps> = ({
  isLeftJoin,
  setIsLeftJoin,
  size = "sm",
}) => (
  <ToggleButtonGroup
    size={size}
    variant="timeframe"
    value={isLeftJoin ? "left_join" : "right_join"}
    onChange={(value) => setIsLeftJoin(value === "left_join")}
    isRequired={true}
  >
    <ToggleButton value="left_join">
      Left Join
      <ToggleButtonTooltip label="Finds all records in customer one and best effort compares to customer two" />
    </ToggleButton>
    <ToggleButton value="right_join">
      Right Join
      <ToggleButtonTooltip label="Finds all records in customer two and best effort compares to customer one" />
    </ToggleButton>
  </ToggleButtonGroup>
);

export const InternalIntegrationTestingIndividualReportPage: React.FC = () => {
  const { t } = useTranslation(["internal", "common"]);
  const { integrationTestingReportId, integrationIndividualTestingReportId } =
    useRequiredStringParams();

  const [isLeftJoin, setIsLeftJoin] = useState<boolean>(true);

  const { data: individualTestingReportData } =
    useInternalIntegrationIndividualTestingReportRetrieveSuspense(
      integrationTestingReportId,
      integrationIndividualTestingReportId,
    );

  const { data: tableData } =
    useInternalIntegrationIndividualTestingReportTestRecordTableSuspense(
      integrationTestingReportId,
      integrationIndividualTestingReportId,
    );

  const { data: comparisonTableData } =
    useInternalIntegrationIndividualTestingReportTestComparisonTableSuspense(
      integrationTestingReportId,
      integrationIndividualTestingReportId,
      {
        left_join: isLeftJoin,
      },
    );

  return (
    <PageContent>
      <PageHeader
        title={naiveFormatEnumValue(individualTestingReportData.test_name)}
        badge={getIndividualTestingReportBadge(
          individualTestingReportData.result,
        )}
        backButton={{
          title: t("internal:integrationTestingReport"),
          path: rootRoute({})
            .internal({})
            .integrationTesting({})
            .report({ integrationTestingReportId }).$,
        }}
      />
      <PageBody>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>Record Table</TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <QueryBoundary>
              <DataTable data={tableData} />
            </QueryBoundary>
          </TableBoxBody>
        </TableBox>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>Comparison Table</TableBoxTitle>
            <DataTableBoxActions>
              <IsLeftJoinToggleButton
                isLeftJoin={isLeftJoin}
                setIsLeftJoin={setIsLeftJoin}
              />
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={comparisonTableData} />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
